import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Form, Button, ListGroup } from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BsPlus, BsTrash } from 'react-icons/bs'
import { Autocomplete, TextField } from '@mui/material'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import {
  addModuels,
  updateModules as onUpdateModule
} from 'slices/modules/thunk'
import { AppDispatch } from 'index'
import { Axios } from 'api/axios' // make sure Axios is imported from your custom instance

const permissionOptions = ['View', 'Create', 'Update']

const AddModuleForm = () => {
  const navigate = useNavigate()
  const { pathname, state }: any = useLocation()
  const isEdit = pathname === '/edit-module-form'
  const dispatch = useDispatch<AppDispatch>()

  const [appName, setAppName] = useState('')
  const [newModuleName, setNewModuleName] = useState('')
  const [newModuleActions, setNewModuleActions] = useState<string[]>([])
  const [modules, setModules] = useState<
    { module_name: string; actions: string[] }[]
  >([])
  const [existingAppNames, setExistingAppNames] = useState<string[]>([])

  const [errors, setErrors] = useState<{
    appName?: string
    newModuleName?: string
    newModuleActions?: string
    modules?: string
  }>({})

  useEffect(() => {
    if (isEdit && state) {
      setAppName(state.app_name || '');

      const initialModules = Object.entries(state.module_permissions || {}).map(
        ([module_name, actions]) => ({
          module_name,
          actions: actions as string[],
        })
      );
      setModules(initialModules);
    }

    const fetchExistingApps = async () => {
      try {
        const res = await Axios.get('/modules/apps/');
        const appNames = res.data.map((app: any) =>
          app.app_name?.toLowerCase()
        );
        setExistingAppNames(appNames || []);
      } catch (err) {
        console.error('Failed to fetch app names', err);
      }
    };

    fetchExistingApps();
  }, [isEdit, state]);


  const validateAppName = () => {
    const trimmedName = appName.trim()

    if (!trimmedName) {
      setErrors(e => ({ ...e, appName: 'Please enter App Name' }))
      return false
    }

    const isDuplicate = existingAppNames.includes(trimmedName.toLowerCase())

    if (!isEdit && isDuplicate) {
      setErrors(e => ({ ...e, appName: 'App Name already exists' }))
      return false
    }

    setErrors(e => ({ ...e, appName: undefined }))
    return true
  }

  const validateNewModule = () => {
    let valid = true

    if (!newModuleName.trim()) {
      setErrors(e => ({ ...e, newModuleName: 'Please enter Module Name' }))
      valid = false
    } else if (
      modules.some(
        mod =>
          mod.module_name.trim().toLowerCase() ===
          newModuleName.trim().toLowerCase()
      )
    ) {
      setErrors(e => ({ ...e, newModuleName: 'Module name already exists' }))
      valid = false
    } else {
      setErrors(e => ({ ...e, newModuleName: undefined }))
    }

    if (newModuleActions.length < 1) {
      setErrors(e => ({
        ...e,
        newModuleActions: 'Please select at least one permission'
      }))
      valid = false
    } else {
      setErrors(e => ({ ...e, newModuleActions: undefined }))
    }

    return valid
  }

  const validateModulesList = () => {
    if (modules.length === 0) {
      setErrors(e => ({
        ...e,
        modules: 'Please add at least one module'
      }))
      return false
    }
    setErrors(e => ({ ...e, modules: undefined }))
    return true
  }

  const handleAddModule = () => {
    if (!validateNewModule()) return

    setModules([
      ...modules,
      { module_name: newModuleName.trim(), actions: newModuleActions }
    ])
    setNewModuleName('')
    setNewModuleActions([])
  }

  const handleRemoveModule = (index: number) => {
    const updated = [...modules]
    updated.splice(index, 1)
    setModules(updated)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const isAppNameValid = validateAppName()
    const isModulesListValid = validateModulesList()

    if (!isAppNameValid || !isModulesListValid) return

    const module_permissions: Record<string, string[]> = modules.reduce(
      (acc, mod) => {
        acc[mod.module_name] = mod.actions
        return acc
      },
      {} as Record<string, string[]>
    )

    const payload = {
      app_name: appName.trim(),
      module_permissions
    }


    try {
      if (isEdit) {
        await dispatch(
          onUpdateModule({ id: state?.module_id, ...payload })
        ).unwrap()

        toast.success('Module updated successfully')
      } else {
        await dispatch(addModuels(payload)).unwrap()
        toast.success('Module added successfully')
      }
      navigate('/module-list')
    } catch (error: any) {
      const status = error?.response?.status
      const data = error?.response?.data
      const backendMessage = data?.message || data?.error || error?.message

      if (status === 404 && backendMessage) {
        toast.error(backendMessage)
      } else if (
        status === 409 ||
        backendMessage?.toLowerCase().includes('exist')
      ) {
        toast.error(backendMessage)
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    }
  }

  return (
    <Container
      fluid
      className='px-4'
      style={{
        backgroundColor: '#f8f9fa',
        minHeight: '100%',
        marginTop: '6rem'
      }}
    >
      <div
        className='bg-white rounded shadow-sm p-4 mx-auto'
        style={{ maxWidth: '1200px' }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 text-muted">
          {/* Left: Back + Heading */}
          <div className="d-flex align-items-center gap-2">
            <span
              style={{ fontSize: '24px', cursor: 'pointer' }}
              onClick={() => navigate(-1)}
            >
              ←
            </span>
            <h5 className="fw-bold mb-0">{isEdit ? 'Update' : 'Add'} Module</h5>
          </div>

          {/* Right: Breadcrumb */}
          <div className="text-muted" style={{ fontSize: "0.9rem" }}>
            <Link
              to="/module-list"
              className="text-decoration-none text-muted me-1"
            >
              Module
            </Link>
            {`> ${isEdit ? 'Update' : 'Add'} Module`}
          </div>
        </div>


        <Form onSubmit={handleSubmit}>
          <Row className='mb-4'>
            <Col md={6}>
              <Form.Group controlId='appName'>
                <Form.Label className='fw-semibold'>
                  App Name
                  <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter App Name'
                  value={appName}
                  onChange={e => setAppName(e.target.value)}
                  onBlur={validateAppName}
                  isInvalid={!!errors.appName}
                  
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.appName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className='mb-3 align-items-end'>
            <Col md={6}>
              <Form.Group controlId='moduleName'>
                <Form.Label className='fw-semibold'>
                  Module Name
                  <span className='text-danger'>*</span>
                </Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Enter Module Name'
                  value={newModuleName}
                  onChange={e => setNewModuleName(e.target.value)}
                  onBlur={validateNewModule}
                  isInvalid={!!errors.newModuleName}
                />
                <Form.Control.Feedback type='invalid'>
                  {errors.newModuleName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId='permissions'>
                <Form.Label className='fw-semibold'>
                  Permissions
                  <span className='text-danger'>*</span>
                </Form.Label>
                <Autocomplete
                  multiple
                  options={permissionOptions}
                  value={newModuleActions}
                  onChange={(_, newValue) => setNewModuleActions(newValue)}
                  onBlur={validateNewModule}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        newModuleActions?.length === 0
                          ? 'Select permissions'
                          : ''
                      }
                      size='small'
                      error={!!errors.newModuleActions}
                      helperText={errors.newModuleActions}
                    />
                  )}
                  disableCloseOnSelect
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className='mb-4'>
            <Col md={6}>
              <Button
                variant='light'
                onClick={handleAddModule}
                className='d-flex align-items-center gap-2 px-4 py-2 fw-semibold'
                type='button'
                disabled={
                  !newModuleName.trim() ||
                  modules.some(
                    mod =>
                      mod.module_name.trim().toLowerCase() ===
                      newModuleName.trim().toLowerCase()
                  )
                }
              >
                <BsPlus size={20} />
                Add
              </Button>
            </Col>
          </Row>

          {modules.length > 0 && (
            <>
              <h6>Added Modules</h6>
              <ListGroup className='mb-4'>
                {modules.map((mod, index) => (
                  <ListGroup.Item
                    key={index}
                    className='d-flex justify-content-between align-items-center'
                  >
                    <div>
                      <strong>{mod.module_name}</strong> - Permissions:{' '}
                      {mod.actions.join(', ')}
                    </div>
                    <Button
                      variant='danger'
                      size='sm'
                      onClick={() => handleRemoveModule(index)}
                    >
                      <BsTrash />
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}
          {errors.modules && (
            <div className='text-danger mb-3'>{errors.modules}</div>
          )}

          <Row>
            <Col className='d-flex justify-content-center gap-3'>
              <Button
                variant='outline-secondary'
                style={{
                  color: '#878899',
                  borderColor: '#B0BAC9',
                  backgroundColor: 'transparent'
                }}
                onClick={() => navigate(-1)}
                type='button'
              >
                Cancel
              </Button>
              <Button variant='primary' type='submit'>
                {isEdit ? 'Update' : 'Add'}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Container>
  )
}

export default AddModuleForm
